import React from 'react';
import { Profile, UnsavedProfile } from '../ProfileForm';
import ListItem from './ListItem';
import ProfileModal from './ProfileModal';
import PhoneNumberForm from './PhoneNumberForm';
import { customAuthPhoneNumberVerify } from '../../../util/customAuth';
import { useFirebase } from '../../../services/firebase';
import {
  getAuth,
  PhoneAuthCredential,
  PhoneAuthProvider,
  RecaptchaVerifier,
  unlink,
} from 'firebase/auth';
import PhoneSMSVerificationForm from '../../../views/login/phone/PhoneSMSVerificationForm';
import { useIonToast } from '@ionic/react';
import { warning } from 'ionicons/icons';
import styled from 'styled-components';

interface NameProps {
  profile: Profile | UnsavedProfile;
  onSave: (
    phoneNumber: string,
    authCredential: PhoneAuthCredential
  ) => Promise<boolean>;
}

const PhoneNumberFormWrapper = styled.div`
  @media (max-width: 480px) {
    padding: 0 24px;
  }
`;

const PhoneNumber: React.FC<NameProps> = ({ profile, onSave }) => {
  const { app, currentUser } = useFirebase();
  const [phoneNumber, setPhoneNumber] = React.useState<string>(
    profile.phoneNumber || ''
  );
  const [present] = useIonToast();
  const [mode, setMode] = React.useState<'edit' | 'verify'>('edit');
  const [verificationId, setVerificationId] = React.useState<string>();

  const clearPhoneNumber = async () => {
    try {
      const updatedUser = await unlink(
        currentUser!,
        PhoneAuthProvider.PROVIDER_ID
      );
      if (updatedUser) {
        present({
          message: 'Phone number unlinked successfully',
          duration: 3000,
          color: 'success',
        });
        return;
      } else {
        throw new Error('Failed to unlink phone number');
      }
    } catch (e) {
      console.error(e);
      present({
        message: (e as any).message || 'Error unlinking phone number',
        duration: 6000,
        color: 'danger',
        icon: warning,
      });
      throw e;
    }
  };

  const updatePhoneNumber = async (
    phoneNumber: string,
    recaptcha: RecaptchaVerifier
  ) => {
    try {
      const auth = getAuth(app);
      const verificationId = await customAuthPhoneNumberVerify(
        auth,
        phoneNumber,
        recaptcha
      );
      setVerificationId(verificationId);
      setPhoneNumber(phoneNumber);
      setMode('verify');
    } catch (e) {
      console.error(e);
      present({
        message: (e as any).message || 'Error sending verification code',
        duration: 6000,
        color: 'danger',
        icon: warning,
      });
    }
  };

  const CONTENT = {
    edit: {
      title: 'Phone Number',
      content: () => (
        <PhoneNumberFormWrapper>
          <PhoneNumberForm
            phoneNumber={profile.phoneNumber || ''}
            onSubmit={async (values, recaptcha) => {
              if (!values.phoneNumber) {
                return clearPhoneNumber();
              } else {
                return updatePhoneNumber(values.phoneNumber, recaptcha);
              }
            }}
          />
        </PhoneNumberFormWrapper>
      ),
    },
    verify: {
      title: 'Verify Phone Number',
      content: (close: () => void) => (
        <PhoneNumberFormWrapper>
          <PhoneSMSVerificationForm
            verificationId={verificationId!}
            onSubmit={async ({ authCredential }) => {
              const success = await onSave(phoneNumber, authCredential);
              if (success) {
                close();
                setMode('edit'); // reset on success
              }
            }}
            goBack={() => setMode('edit')}
          />
        </PhoneNumberFormWrapper>
      ),
    },
  };

  return (
    <>
      <ListItem
        id="phoneNumber"
        title="Phone Number"
        value={profile.phoneNumber}
      />

      <ProfileModal title="Phone Number" trigger="phoneNumber">
        {({ close }) => CONTENT[mode].content(close)}
      </ProfileModal>
    </>
  );
};

export default PhoneNumber;
